/**
 * Hand-offs to the person in charge (core handoff.ts; docs/ux/LAUNCH-PROPOSALS.md Wave 1 item 5).
 *  - PutInCharge: the button on an On-shift row (managers and owners) → PATCH isPersonInCharge.
 *  - HandoffShift: "Hand off the shift", a real button (close moment): pick who, then it's on their list.
 *  - InCharge: what a staff member in charge (a shift lead) sees on For You: who's on shift, and the hand-off.
 * Push isn't built, so every line says whose list it's on and to tell them in person.
 */
import { useState } from "react";
import type { Employee } from "@logwave/core";
import type { Gate, TodayResponse } from "../api.ts";
import { Button, Field, Notice, Pill } from "./ui.tsx";
import { saveErrorText } from "../lib/failures.ts";
import { shiftStatus } from "../lib/meaning.ts";
import { useApi, useSession } from "../session.tsx";

/** "Put in charge" on an On-shift row. Managers and owners only; not on the row of whoever is already in charge. */
export function PutInCharge({ e, onDone }: { e: Employee; onDone?: () => void | Promise<void> }) {
  const api = useApi();
  const s = useSession();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  if (!s.can("manager")) return null;
  if (e.isPersonInCharge) return null; // the row already says "person in charge"
  const first = e.name.split(" ")[0];
  async function go() {
    setBusy(true); setErr(undefined);
    try { await api.updateEmployee(s.org.id, e.id, { isPersonInCharge: true }); await onDone?.(); }
    catch (x) { setErr(saveErrorText(x)); } finally { setBusy(false); }
  }
  return (
    <>
      <Button small variant="secondary" icon="people" busy={busy} onClick={() => void go()} aria-label={`Put in charge: ${first}`}>Put in charge</Button>
      {err && <span className="field-msg" role="alert">{err}</span>}
    </>
  );
}

/** "Hand off the shift": a real button. Defaults to the person the server would route to; any other person on shift can be picked. */
export function HandoffShift({ t, onDone }: { t: TodayResponse; onDone?: () => void | Promise<void> }) {
  const api = useApi();
  const s = useSession();
  const others = t.onShift.map((o) => o.employee).filter((e): e is Employee => !!e && e.id !== s.employeeId);
  const choices = [...(t.handoff?.to ?? []).map((p) => ({ id: p.employeeId, name: p.name })), ...others.map((e) => ({ id: e.id, name: e.name }))]
    .filter((c, i, all) => all.findIndex((x) => x.id === c.id) === i);
  const [to, setTo] = useState<string>(choices[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const [done, setDone] = useState<string>();
  if (done) return <Notice tone="go" title="Shift handed off">{done}</Notice>;
  async function go() {
    setBusy(true); setErr(undefined);
    try {
      const r = await api.handoff(s.org.id, { locationId: s.location.id, ...(to ? { toEmployeeId: to } : {}) });
      setDone(r.line);
      await onDone?.();
    } catch (x) { setErr(saveErrorText(x)); } finally { setBusy(false); }
  }
  return (
    <div className="stack-sm" data-testid="handoff-shift">
      {choices.length > 1 && (
        <Field label="Hand it to">{(id) => <select id={id} value={to} onChange={(e) => setTo(e.target.value)}>{choices.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}</Field>
      )}
      <Button block icon="handoff" busy={busy} disabled={!choices.length} reason="Nobody else is here to take it. Put someone in charge on Team." onClick={() => void go()}>
        {choices.length === 1 ? `Hand off the shift to ${choices[0]!.name.split(" ")[0]}` : "Hand off the shift"}
      </Button>
      {err && <p className="field-msg" role="alert">{err}</p>}
      <p className="caption">It goes on their list with what's still open. Tell them in person too.</p>
    </div>
  );
}

/** For You, for a staff member in charge (a shift lead): who's on shift, and at close the hand-off. */
export function InCharge({ t, closing, onDone }: { t: TodayResponse; closing: boolean; onDone?: () => void | Promise<void> }) {
  const s = useSession();
  return (
    <div className="stack-sm" data-testid="in-charge">
      <ul className="people">
        {t.onShift.filter((o) => !!o.employee).map(({ employee: e, gate }) => {
          const st = shiftStatus(gate as Gate);
          return (
            <li key={e.id} className="person-row">
              <span className="avatar" aria-hidden="true">{e.name.slice(0, 1)}</span>
              <span className="grow"><span className="person-name">{e.name}{e.id === s.employeeId ? " (you)" : ""}</span></span>
              <Pill tone={st.tone} icon={st.icon}>{st.label}</Pill>
            </li>
          );
        })}
      </ul>
      {closing ? <HandoffShift t={t} onDone={onDone} /> : <p className="caption">Reports and fixes from the team land on your list here. At closing, you hand off the shift.</p>}
    </div>
  );
}
